
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import Amap from '../../../AMap';
import styles from './index.module.less';
import ProvinceList from '../../../ProvinceList';
import SearchInMap from '../../SearchInMap';
import { useAmapLevelAndCityNew } from '@/common/hook/useAmapLevelAndCityNew';
import { CITY_LEVEL, CQ_CODE_SUBURB, DISTRICT_LEVEL, PROVINCE_LEVEL } from '../../../AMap/ts-config';
import { useMethods } from '@lhb/hook';
import { setStorage } from '@lhb/cache';
import { CITY_ZOOM, CQ_SUBURB_NAME, DISTRICT_ZOOM, PROVINCE_ZOOM } from '../../../Map/V2AMap/utils/amap';
import { debounce, isArray, isEqual, isNotEmptyAny } from '@lhb/func';
import { CircleBusinessZoom, MapMarkerType, TargetZoom } from './ts-config';
import { calculateViewportCorners } from '@/common/utils/ways';
import { geUnderTheDistrictPagingData, getSiteModelList } from '@/common/api/networkplan';
import { markerActiveColorOption, markerDefaultColorOption } from '@/common/enums/business/siteselectionmap';
import IconFont from '@/common/components/IconFont';
import { v4 } from 'uuid'; // 用来生成不重复的key
import ReactDOM from 'react-dom';
import { Typography } from 'antd';
import RimShop from './RimShop';
import { useBigdataFn } from '@/common/utils/bigdata';
import { useDebounceEffect } from '@/common/hook/useEffectDebounce';
import { useCurLocation } from '@/common/hook/Amap/useCurLocation';

interface CirclebusinessMapProps {
  form,
  /** 是否从选址地址创建商圈 */
  isBusiness?: boolean,
  /** 选中地址商圈信息 */
  businessInfo?: any,
}

/** 商圈地图 */
const CirclebusinessMap = forwardRef((props:CirclebusinessMapProps, ref) => {
  const bigdataFn = useBigdataFn(); // 使用埋点 Hook
  const { form, isBusiness, businessInfo } = props;
  const [mapIns, setMapIns] = useState<any>(null);// 地图实例
  const [districtList, setDistrictList] = useState<Array<any>>([]);
  const mapHelpfulInfo = useAmapLevelAndCityNew(mapIns, true, true); // 监听地图相关的数据
  const { city, level } = mapHelpfulInfo;
  const selectListRef = useRef<any>([]); // 存储选择的项包含名称之类
  const changeLockRef = useRef<boolean>(true); // 用来准确判断是修改了下拉框选项，然后去跳转位置
  const cityRef = useRef<any>(null); // 存储城市信息
  const businessAreaRailRef = useRef<any>(null); // 记录当前商圈围栏信息
  const curViewMarkerRef: any = useRef(); // 当前查看的商圈id
  const isShowToastMsg = useRef<boolean>(false);
  const [updateShopList, setUpdateShopList] = useState<number>(0); // 地图移动结束后标记是否获取周边500的门店的state
  const [modelConfigId, setModelConfigId] = useState<number>(0);
  const modelConfigIdRef: any = useRef(0);
  const massMarkerRef = useRef<any>(null); // 海量点
  const commonParamsRef = useRef<any>();// 缓存上一次的mapParams
  const [portData, setPortData] = useState<any[]>([]); // 当前地图上的开店区域原始数据--未处理（地图-统一）
  const [isLoading, setLoading] = useState(false); // 是否正在请求海量点
  const [massClickData, setMassClickData] = useState<any>(null); // 点击海量点的数据项
  const [loadingCircleMarker, setLoadingCircleMarker] = useState(false); // 是否正在加载商圈围栏，加载完需配合massClickData设置焦点
  const locationInfo = useCurLocation(mapIns, {
    panToLocation: !isBusiness, // 定位成功后是否自动移动到响应位置
    zoomToAccuracy: false, // 定位成功后是否自动调整级别
    showCircle: false // 不显示定位的蓝色圈
  });
  useEffect(() => {
    if (!mapIns) return;
    // 加载商圈详情的商圈围栏
    if (isBusiness) {
      if (!isNotEmptyAny(businessInfo)) return;
      const { lng, lat } = businessInfo;
      flyToCurrentPos(lng, lat);
    }
    mapIns.on('moveend', handleSearchCircle);
    mapIns.on('zoomend', debounceMapZoomChange);
    return () => {
      mapIns.off('moveend', handleSearchCircle);
      mapIns?.off('zoomend', debounceMapZoomChange);
    };
  }, [mapIns]);

  useDebounceEffect(() => {
    if (!modelConfigId) {
      getModelConfigId();
      return;
    }
    if (isBusiness) return;
    if (city && level && !isLoading) {
      const params = {
        cityIds: [city.id],
        provinceIds: [city.provinceId],
        level,
        page: 1,
        secondLevelCategories: [],
        firstLevelCategories: [],
        showPolygon: false,
        size: 500,
        onlyLatLng: true,
        sort: 'desc',
        sortField: null,
        sortRule: 1,
        modelConfigId: modelConfigId
      };
      commonParamsRef.current = {
        ...params,
      };
      setLoading(true);
      loadDistrictData(params);
    }
  }, [city, level, isLoading, modelConfigId], 300);

  useEffect(() => {
    if (isBusiness) return;
    const select: any = [];
    const cityName:any = [];
    (level >= PROVINCE_LEVEL) && city?.provinceId && select.push(city.provinceId) && cityName.push(city.province);
    (level >= CITY_LEVEL) && city?.id && select.push(city.id) && cityName.push(city.city || city.province);
    (level >= DISTRICT_LEVEL) && city?.districtList?.forEach(district => {
      if (district.name === city.district) {
        select.push(district.id);
        cityName.push(district.name);
      }
    });
    handledistrict(select, cityName);
  }, [city, level]);

  useEffect(() => {
    if (cityRef.current?.city?.name !== city?.name) {
      setLoading(false);
    }
    cityRef.current = {
      city,
      level
    };
  }, [city, level]);

  // 暴露相关方法
  useImperativeHandle(ref, () => ({
    onChangeProvince, // 点击province
    onDropdownVisibleChange
  }));

  const {
    onChangeProvince,
    onDropdownVisibleChange,
    customCQ,
  } = useMethods({
    onChangeProvince: (val, selectedOptions) => {
      handledistrict(val, selectedOptions?.map(it => it.name));
      selectListRef.current = selectedOptions || [];
      !changeLockRef.current && (changeLockRef.current = true);
      // onChange && onChange(val, selectedOptions);
    },
    onDropdownVisibleChange: (value) => {
      // setIsOpenCascaderMap?.(value);
      // 关闭下拉框时判断是否需要将地图定位
      if (changeLockRef.current && !value) {
        changeLockRef.current = false;
        const district = isArray(selectListRef.current) ? selectListRef.current[selectListRef.current.length - 1] : '';
        // 根据选项的长度判断地图放大级别
        /**
           * 0: 全国
           * 1: 省级
           * 2: 市
           * 3: 区
           */
        const zoomMap = [4, PROVINCE_ZOOM, CITY_ZOOM, DISTRICT_ZOOM];

        if (district) {
          // 处理选择重庆市/重庆郊县的特殊逻辑
          customCQ(district);
          // 提升层级 +2
          mapIns && mapIns.setZoomAndCenter(zoomMap[selectListRef.current.length] + 2, [district.lng, district.lat], false, 300);
        }
      };
    },
    // 处理选择重庆市(省级别)下重庆市(城市级别)/重庆郊县(城市级别)的特殊逻辑
    customCQ: (city: any) => {
      const { code } = city || {};
      if (code !== CQ_CODE_SUBURB) return;
      setStorage('CQ_SUBURB_NAME', CQ_SUBURB_NAME);
    }
  });
  const getModelConfigId = async() => {
    const data: any = await getSiteModelList();
    const targetModelConfigId = isArray(data) ? data[0]?.id : 0;
    if (targetModelConfigId) {
      setModelConfigId(targetModelConfigId);
      modelConfigIdRef.current = targetModelConfigId;
    }
  };

  const handleSearchCircle = debounce(async() => {
    if (!mapIns) return;
    bigdataFn('aa3adc9c-54b8-4c13-a807-ceb225dbeb5b', '创建拓店任务-地图拖动一次', '创建拓店任务-地图拖动一次', { op_block: '创建拓店任务', op_event: ``, });
    const zoom = mapIns.getZoom();
    if (!modelConfigIdRef.current) {
      getModelConfigId();
    }
    if (zoom <= CircleBusinessZoom.MinZomm && !isBusiness) {
      clearOverlayGroup(); // 清楚上一次的图层
      if (!isShowToastMsg.current) {
        if (portData?.length) {
          createPointMarker(portData || []);
        } else {
          loadDistrictData(commonParamsRef.current);
        }
        isShowToastMsg.current = true;
      }
      return;
    }
    setUpdateShopList((state) => state + 1);
    isShowToastMsg.current = false;
    if (isBusiness) {
      if (!isNotEmptyAny(businessInfo)) return;
      const { id } = businessInfo;
      curViewMarkerRef.current = id;
      createBusinessLayers([businessInfo]);
    } else {
      massMarkerRef.current && massMarkerRef.current?.clear();
      const { city, level } = cityRef.current;
      const center = mapIns.getCenter();
      const { topLeft, bottomRight } = calculateViewportCorners(center.lat, center.lng, 1000);
      const _params: any = {
        cityIds: [city.id],
        provinceIds: [city.provinceId],
        level,
        maxLat: topLeft.lat,
        maxLng: bottomRight.lng,
        minLat: bottomRight.lat,
        minLng: topLeft.lng,
        page: 1,
        secondLevelCategories: [],
        firstLevelCategories: [],
        showPolygon: true,
        size: 500,
        onlyLatLng: false,
        sort: 'desc',
        sortField: null,
        sortRule: 1
      };
      if (modelConfigIdRef.current) {
        _params.modelConfigId = modelConfigIdRef.current;
      }
      const businessCircleInfo = await geUnderTheDistrictPagingData(_params);
      if (!isNotEmptyAny(businessCircleInfo)) return;
      createBusinessLayers(businessCircleInfo);
    }

  }, 500);

  // 监听地图缩放
  const debounceMapZoomChange = debounce(() => {
    bigdataFn('3ecacfba-a5b4-48e8-a174-19c92656df1a', '创建拓店任务-地图放大/缩小一次', '创建拓店任务-地图放大/缩小一次', { op_block: '创建拓店任务', op_event: ``, });
  }, 100);
  // 创建商圈和marker
  const createBusinessLayers = (businessInfo) => {
    const markers :Array<any> = [];
    const layers: any = []; // 围栏覆盖物
    businessInfo.forEach((item) => {
      let marker;
      const { radius, polygon } = item;
      if (radius) { // 显示圆
        marker = createBusinessCircle(item); // 绘制商圈围栏（圆）覆盖物
      } else if (isArray(polygon) && polygon.length) { // 显示多边形
        marker = createBusinessPolygon(item); // 绘制商圈围栏（多边形）覆盖物
      }
      marker[1] && markers.push(marker[1]);
      marker && layers.push(...marker);
    });
    const railOverlayGroups = new window.AMap.OverlayGroup(layers); // 围栏覆盖物群组
    mapIns.add(railOverlayGroups); // 添加到地图
    businessAreaRailRef.current && (mapIns.remove(businessAreaRailRef.current)); // 清除上一次的
    businessAreaRailRef.current = railOverlayGroups;
    markers.forEach(item => {
      const data = item?._opts?.extData;
      const preMarkerId = curViewMarkerRef.current;
      const isClick = preMarkerId === data.id;
      renderMarkerContent(data, isClick);
    });
    setLoadingCircleMarker(true);
  };

  // 创建多边形商圈
  const createBusinessPolygon = (item) => {
    const { lng, lat } = item;
    const preMarkerId = curViewMarkerRef.current;
    if (!(+lng && +lat)) return;
    const lnglat = [+lng, +lat];
    // const path = item?.polygon?.map((item) => {
    //   return [+item.lng, +item.lat];
    // });
    const isClick = preMarkerId === item.id;
    const fillColor = isClick ? markerActiveColorOption : markerDefaultColorOption;
    const uid:any = v4();
    const polygon = new window.AMap.Polygon({
      zooms: [CircleBusinessZoom.MinZomm, 20],
      path: item?.polygon,
      strokeOpacity: 1,
      fillOpacity: 0.2,
      strokeWeight: 1,
      strokeStyle: 'dashed',
      // zIndex: 60,
      anchor: 'bottom-center',
      bubble: true,
      extData: {
        id: item.id
      },
      ...fillColor
      // ...(mainBrandsRank < 4 ? markerDefaultTopColorOption : markerDefaultColorOption)
    });
    const marker = new window.AMap.Marker({
      zooms: [CircleBusinessZoom.MinZomm, 20],
      position: lnglat,
      anchor: 'center',
      extData: { uid, ...item },
      content: `<div id=${uid}></div>`

    });
    polygon.on('click', () => businessAreaItemMarkerClick(item));
    marker.on('click', () => businessAreaItemMarkerClick(item));
    return [polygon, marker];
  };
  // 创建圆形商圈
  const createBusinessCircle = (item: any) => {
    const { lng, lat } = item;
    const preMarkerId = curViewMarkerRef.current;
    if (!(+lng && +lat)) return;
    const lnglat = [+lng, +lat];
    const isClick = preMarkerId === item.id;
    const fillColor = isClick ? markerActiveColorOption : markerDefaultColorOption;
    const uid:any = v4();
    const circle = new window.AMap.Circle({
      zooms: [CircleBusinessZoom.MinZomm, 20],
      center: lnglat,
      radius: item?.radius, // 半径（米）
      strokeWeight: 1,
      strokeOpacity: 1,
      fillOpacity: 0.2,
      strokeStyle: 'dashed',
      strokeDasharray: [10, 10],
      bubble: true, // 为了支持在商圈围栏上测距
      extData: item,
      ...fillColor,
      // ...(mainBrandsRank < 4 ? markerDefaultTopColorOption : markerDefaultColorOption)
    });
    const marker = new window.AMap.Marker({
      position: lnglat,
      zooms: [CircleBusinessZoom.MinZomm, 20],
      anchor: 'center',
      extData: { uid, ...item },
      content: `<div id=${uid}></div>`
    });
    circle.on('click', () => businessAreaItemMarkerClick(item));
    marker.on('click', () => businessAreaItemMarkerClick(item));
    return [circle, marker];
  };

  // 商圈|商圈围栏点击事件
  const businessAreaItemMarkerClick = (item) => {
    // const { lng, lat } = item;
    const preMarkerId = curViewMarkerRef.current;
    if (preMarkerId === item.id) return;
    form.setFieldsValue({
      businessName: item.areaName,
      circleBusinessId: item.id
    });
    // 将点击项移动到地图中心，17是初始化时的zoom
    // mapIns.setZoomAndCenter(TargetZoom, [+lng, +lat], false, 200);
    // 重置前一个点击样式
    if (preMarkerId) {
      const premarkersList = getMarkersById(preMarkerId);
      premarkersList.forEach((marks) => {
        if (marks.type === MapMarkerType.Marker) {
          renderMarkerContent(marks?._opts?.extData, false);
          // marks.setContent(renderMarkerContent(marks?._opts?.extData, false));
        } else {
          marks.setOptions(markerDefaultColorOption);
        }
      });
    }
    const markersList = getMarkersById(item.id);
    markersList.forEach((marks) => {
      if (marks.type === MapMarkerType.Marker) {
        renderMarkerContent(marks?._opts?.extData, true);
        // marks.setContent(renderMarkerContent(item, true));
      } else {
        marks.setOptions(markerActiveColorOption);
      }
    });
    curViewMarkerRef.current = item.id;
    // const lastMarker = curViewMarkerRef.current; // 上一个商圈marker
    // const railMarker = targetMarkerInOverlays(id, businessAreaRailRef.current); // 查找商圈围栏marker
  };

  const clearOverlayGroup = () => {
    businessAreaRailRef.current && (mapIns.remove(businessAreaRailRef.current)); // 清除上一次的
  };

  const flyToCurrentPos = (lng, lat) => {
    mapIns && mapIns.setZoomAndCenter(TargetZoom, [+lng, +lat], true);
    if (isBusiness) { // 从选址地图进来需要禁用缩放和拖动
      mapIns.setStatus({
        dragEnable: false,
        zoomEnable: false,
      });
    }
  };
  const getMarkersById = (id) => {
    if (!businessAreaRailRef.current) return [];
    const markers = (businessAreaRailRef.current._overlays as Array<any>).filter((item) => item?._opts?.extData?.id === id) || [];
    return markers;
  };
  const renderMarkerContent = (info, isClick) => {
    const { uid } = info;
    if (!uid) return;
    const Node = <div className={styles.markerWrapper}>
      <div
        className={styles.markerContent} style={{
          background: isClick ? '#FF861D ' : '#006AFF',
        }}>
        <Typography.Text style={{ maxWidth: 165, color: '#fff', fontSize: 12 }} ellipsis={{ tooltip: info.areaName }}>{info.areaName}</Typography.Text>
      </div>
      <div className={styles.point} style={{
        borderTopColor: isClick ? '#FF861D ' : '#006AFF'
      }}/>
    </div>;
    return ReactDOM.render(Node, document.getElementById(uid));
  };

  // 地图加载完成
  const mapLoadedHandle = (ins: any) => {
    ins && setMapIns(ins);
  };

  const handledistrict = (province, selectedOptions?:any) => {
    const currentProvincesStr = province?.join('') || '';
    const preProvincesStr = form.getFieldValue('targetAddress')?.join('') || '';
    // 判断是否是同一个省市区
    const isSameProvince = currentProvincesStr === preProvincesStr;
    const setValues = {
      targetAddress: province || [],
      targetAddressInfo: selectedOptions || []
    };
    if (!isSameProvince) {
      Object.assign(setValues, { businessName: '' });
      curViewMarkerRef.current = null;
    }
    form.setFieldsValue(setValues);
    setDistrictList(province || []);
  };

  const handleClickPosition = () => {
    if (!isBusiness) {
      flyToCurrentPos(locationInfo.lng, locationInfo.lat);
      return;
    }
    if (!isNotEmptyAny(businessInfo)) return;
    const { lng, lat } = businessInfo;
    clearOverlayGroup();
    flyToCurrentPos(lng, lat);
    createBusinessLayers([businessInfo]);
  };

  const createPointMarker = (val) => {
    // 创建一个 Marker 实例：
    const data:any = [];
    val.map((item) => {
      data.push({
        ...item,
        lnglat: [+item.lng, +item.lat]
      });
    });
    const mass = new window.AMap.MassMarks(data, {
      style: {
        url: `${process.env.STATIC_RES_DOMAIN}/project-custom/locationpc/map/circle_blue_icon@3x.png`,
        size: new window.AMap.Size(10, 10),
        anchor: new window.AMap.Pixel(5, 5),
      },
      zIndex: 500, // 不然会被showIndoorMap遮挡
    });
    massMarkerRef.current && massMarkerRef.current?.clear();
    massMarkerRef.current = mass;
    mass.on('click', function (e) {
      const { data = {} } = e;
      mapIns && mapIns.setZoomAndCenter(16, [data.lng, data.lat], false, 300);
      setTimeout(() => {
        setLoadingCircleMarker(false);
        setMassClickData(data);
        form.setFieldsValue({
          businessName: data.areaName,
          circleBusinessId: data.id
        });
        isShowToastMsg.current = false;
      }, 300);
    });
    mass.setMap(mapIns);
  };

  // 获取区级以下的数据(分页接口，通过并行请求及递归获取全量数据)
  const loadDistrictData = async (
    params,
    curPage = 1,
    lastData:any[] = []
  ) => {
    if (isBusiness) return; // 由选址地图进来不请求其他机会点数据
    const size = 500; // 由接口调试后得出的分页size
    const _params = {
      ...params,
      showPolygon: false,
      onlyLatLng: true, // 返回的数据只包含经纬度（数据量大的时候该参数设为true，提升接口响应速度），该参数会在第一次请求时被设为false，用来判断是否需要聚合
      size,
      // 接口(夏奇)不再处理isSelectMap参数，统一按照权限控制
      // isSelectMap: false, // 表明是选址地图-不受权限控制
    };
    Promise.all([
      geUnderTheDistrictPagingData({ ..._params, page: curPage }),
      geUnderTheDistrictPagingData({ ..._params, page: curPage + 1 }),
      geUnderTheDistrictPagingData({ ..._params, page: curPage + 2 }),
    ]).then((res) => {
      if (!isEqual(commonParamsRef.current, {
        ...params,
      })) {
        loadDistrictData(commonParamsRef.current, 1, []);
        return;
      }
      const newArr = [...lastData, ...res[0], ...res[1], ...res[2]];
      // 最后一个请求依然有size的数据，可能还有更大的页数，进行递归调用
      // curPage最大30页，超过30页后不请求
      if (res[2]?.length === size && curPage <= 30) {
        loadDistrictData(params, curPage + 3, newArr);
        return;
      }
      setPortData(newArr);
    });
  };

  useEffect(() => {
    if (portData?.length) {
      createPointMarker(portData);
    }
  }, [portData]);

  useEffect(() => {
    if (massClickData && loadingCircleMarker) {
      setMassClickData(null);
      setLoadingCircleMarker(false);
      businessAreaItemMarkerClick(massClickData);
    }
  }, [massClickData, loadingCircleMarker]);

  return <div className={styles.mapWrapper}>
    {/*  */}
    <Amap
      mapOpts={{
        zooms: [10, 18],
        zoom: 14,
      }}
      loaded={mapLoadedHandle}
      plugins={[
        'AMap.DistrictSearch',
        'AMap.PlaceSearch',
        'AMap.AutoComplete',
      ]}
    >
      {/* 周边500米已签约、已交房、开业中、已闭店的门店  */}
      <RimShop
        needLoadData={updateShopList}
        modelConfigId={modelConfigId}
      />
    </Amap>
    <div className={styles.toolBar}>
      {
        !isBusiness && <>
          <ProvinceList
            className={styles.provinceList}
            type={1}
            value={districtList}
            onChange={onChangeProvince}
            changeOnSelect
            onDropdownVisibleChange={onDropdownVisibleChange}
            allowClear={false}
          />
          <SearchInMap
            mapIns={mapIns}
            mapHelpfulInfo={mapHelpfulInfo}
            hideBorder
            autoCompleteConfig={{
              style: {
                marginLeft: 8
              }
            }}
            finallySelected={() => {
              bigdataFn('24686756-6e85-4219-a35c-06603002bf21', '创建拓店任务-搜索具体地址', '点击了创建拓店任务-搜索具体地址', { op_block: '创建拓店任务', op_event: ``, });
            }}
            searchConfig={{
              style: {
                width: 280,
                height: 37
              },
            }}/>
        </>
      }
    </div>
    <div className={styles.toolBottomBar}>
      <IconFont
        iconHref='iconic_mendiangengzhong'
        className='c-666'
        onClick={handleClickPosition}
      />
    </div>
  </div>;
});


export default CirclebusinessMap;
